import { useLocation } from 'react-router-dom';

const Footer = () => {
  const location = useLocation();
  const isEnglish = location.pathname === '/en' || location.pathname === '/en/' || location.pathname.startsWith('/en/');

  const withEn = (path: string): string => {
    if (!isEnglish) return path;
    if (!path.startsWith('/')) return path;
    if (path.startsWith('/en')) return path;
    if (path.startsWith('/blog')) return path;
    if (path.startsWith('http')) return path;
    if (path.startsWith('#')) return path;
    if (path === '/') return '/en';
    return '/en' + path;
  };

  return (
    <footer className="bg-graphite border-t border-metal mt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Site Info */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/assets/nag-logo-512.png" 
                alt={isEnglish ? "Sekisou NAG Note Logo" : "せきそう NAG ノート ロゴ"}
                className="w-8 h-8 object-contain"
              />
              <h3 className="text-lg font-bold text-white font-['Exo']">せきそう NAG ノート</h3>
            </div>
            <p className="text-metal-light text-sm leading-relaxed">
              {isEnglish 
                ? "A personal research notebook and archive site documenting FDM 3D printer output conditions, material properties, measured data, and troubleshooting solutions."
                : "個人的に運用しているFDM式3Dプリンターの出力条件・材料特性・実測データ・トラブル対策を整理した研究ノート兼アーカイブサイト。"
              }
            </p>
            <div className="tech-label inline-block">
              SEKISO NAG NOTE
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-white font-semibold font-['Exo'] accent-line pb-2">
              {isEnglish ? "Categories" : "記録カテゴリ"}
            </h4>
            <nav className="space-y-2">
              <a href={withEn('/filament')} className="block text-metal-light hover:text-accent transition-colors text-sm">
                <i className="ri-database-line mr-2"></i>{isEnglish ? "Filament Data" : "フィラメントデータ"}
              </a>
              <a href={withEn('/troubleshooting')} className="block text-metal-light hover:text-accent transition-colors text-sm">
                <i className="ri-tools-line mr-2"></i>{isEnglish ? "Troubleshooting" : "トラブル対策"}
              </a>
              <a href="/blog" className="block text-metal-light hover:text-accent transition-colors text-sm">
                <i className="ri-article-line mr-2"></i>{isEnglish ? "Blog" : "ブログ"}
              </a>
              <a href={withEn('/beginner')} className="block text-metal-light hover:text-accent transition-colors text-sm">
                <i className="ri-graduation-cap-line mr-2"></i>{isEnglish ? "For Beginners" : "初心者向け"}
              </a>
              <a href={withEn('/knowledge')} className="block text-metal-light hover:text-accent transition-colors text-sm">
                <i className="ri-lightbulb-line mr-2"></i>{isEnglish ? "Useful Knowledge" : "役立ち関連知識"}
              </a>
            </nav>
          </div>

          {/* Technical Info */}
          <div className="space-y-4">
            <h4 className="text-white font-semibold font-['Exo'] accent-line pb-2">
              {isEnglish ? "Equipment Used" : "使用機材"}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2">
                <i className="ri-printer-line text-accent"></i>
                <a
                  href="https://elegoo.sjv.io/dOXABQ"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-metal-light hover:text-accent transition-colors font-['Roboto_Mono']"
                >
                  ELEGOO Centauri Carbon
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-database-line text-accent"></i>
                <span className="text-metal-light font-['Roboto_Mono']">{isEnglish ? "Measured Data Recording" : "実測データ記録"}</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-test-tube-line text-accent"></i>
                <span className="text-metal-light font-['Roboto_Mono']">{isEnglish ? "Material Property Testing" : "材料特性試験"}</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="ri-file-text-line text-accent"></i>
                <span className="text-metal-light font-['Roboto_Mono']">{isEnglish ? "Personal Research Notes" : "個人研究ノート"}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="section-divider"></div>

        {/* Disclaimer and Affiliate Notice */}
        <div className="bg-metal/30 border border-metal rounded-lg p-4 mb-6">
          <div className="space-y-2">
            <p className="text-metal-light text-xs leading-relaxed">
              <i className="ri-information-line text-accent mr-2"></i>
              {isEnglish 
                ? "※The operator assumes no responsibility for the results of using the information on this site. Please use it at your own risk."
                : "※本サイトの情報を利用した結果について、運営者は一切の責任を負いません。必ず自己責任でご利用ください。"
              }
            </p>
            <p className="text-metal-light text-xs leading-relaxed">
              {isEnglish 
                ? "Links on this site may include affiliate links. Purchases made through these links may result in compensation to the operator."
                : "当サイトのリンクには、アフィリエイトリンクが含まれている場合があります。リンク経由の購入により、運営者に報酬が発生することがあります。"
              }
            </p>
          </div>
        </div>

        {/* Site Links */}
        <div className="flex flex-wrap justify-center gap-4 mb-6">
          <a href={withEn('/privacy')} className="text-metal-light hover:text-accent transition-colors text-xs">
            {isEnglish ? "Privacy Policy" : "プライバシーポリシー"}
          </a>
          <div className="w-1 h-1 bg-metal rounded-full self-center"></div>
          <a href={withEn('/disclaimer')} className="text-metal-light hover:text-accent transition-colors text-xs">
            {isEnglish ? "Disclaimer" : "免責事項"}
          </a>
          <div className="w-1 h-1 bg-metal rounded-full self-center"></div>
          <a href={withEn('/data-policy')} className="text-metal-light hover:text-accent transition-colors text-xs">
            {isEnglish ? "Data Usage Policy" : "データ利用規約"}
          </a>
          <div className="w-1 h-1 bg-metal rounded-full self-center"></div>
          <a href={withEn('/contact')} className="text-metal-light hover:text-accent transition-colors text-xs">
            {isEnglish ? "Contact" : "お問い合わせ"}
          </a>
        </div>

        {/* Bottom */}
        <div className="flex flex-col items-center">
          <div className="text-metal-light text-sm font-['Roboto_Mono'] text-center">
            © 2026 せきそう NAG ノート / sekisonagnote.jp. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
